import { useState, useEffect, useReducer, useMemo } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useSelector, batch } from 'react-redux'
import { max, flatten, isNumber, isString, sortBy } from 'lodash-es'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import InfiniteScroll from 'react-infinite-scroll-component'
import LazyLoad from 'react-lazyload'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import styled from '@emotion/styled'
import useMeasureHeight from 'utils/hooks/useMeasureHeight'

import {
  useVenders,
  useVenderCount,
  useInfiniteVenders,
  useServiceClassification,
  useAdvancedFilterItems,
  useVenderServiceCapacity,
} from 'utils/api/hooks'
import { getServiceClassificationById } from 'utils/api'
import { useAlbumModal } from 'utils/hooks'
import { GtmAgent } from 'utils/hooks/analytics'
import { selectWebview } from 'store/webview/webviewSlice'
import { LS_APP_DOMAIN } from 'config/constants'
import addressFactory from 'utils/address'
import { getFilterInitialState, filterReducer } from './hooks'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import Footer from 'components/common/Footer'
import InfiniteContainer from 'components/common/InfiniteContainer'
import ContentWrapper, {
  MaxWidthWrapper,
} from 'components/common/ContentWrapper'
import Pagination from 'components/common/Pagination'
import GutterGrid from 'components/common/GutterGrid'
import { useStickyFilter } from 'components/common/StickyScroll'
import Select from 'components/common/Select'
import {
  HideInMobileGrid,
  HideInMobileBox,
  HideInDesktopBox,
  HideInDesktopGrid,
} from 'components/common/Hidden'
import { FullscreenModal } from 'components/common/Modal'
import { CloseSVG, ArrowRight } from 'components/common/SVGElements'
import VenderListItem from './VenderListItem'
import { VENDER_AD_WARNING_TEXT } from 'components/pages/Vender/constants'
import ServiceClassificationSelectRadio from './ServiceClassificationSelectRadio'
import AdvancedFilterSelection from './AdvancedFilterSelection'
import NowrapDiv from 'components/common/NowrapDiv'
import Link from 'components/common/A'

const DynamicAdvanceFilters = dynamic(
  () => import('components/pages/Vender/VenderList/AdvanceFilters'),
  {
    ssr: false,
  }
)

const DynamicAlbumContent = dynamic(() =>
  import('components/pages/Album/AlbumContent')
)

const areaData = addressFactory()

export const NUMBER_PER_PAGE = 10

const Img = styled.img`
  width: 236px;
  max-width: 100%;
`

const Sticky = styled.div`
  position: sticky;
  margin-bottom: 0;
  top: ${({ isSticky, isVisible, topOffset }) =>
    isSticky && isVisible ? `${topOffset}px` : '0px'};
`

const Rect = styled.div`
  position: relative;
  padding-top: 50%;
`

const Tag = styled.div`
  ${({ theme }) => `
    padding: 10px 12px;
    border-radius: 20px;
    border: 1px solid ${theme.palette.background.line};
  `}
`

const VenderList = ({ isMobile, fallbackData }) => {
  // filtering
  const [filterState, filterDispatch] = useReducer(
    filterReducer,
    getFilterInitialState(fallbackData)
  )

  const onServiceClassificationChange = (id, name) => {
    // 判斷是否需要檢查 id === filterState.serviceItem
    const shouldCheckSameId = isLarge ? id === filterState.serviceItem : false

    if (!id || shouldCheckSameId) {
      batch(() => {
        filterDispatch({ type: 'REMOVE_SERVICE_ITEM' })
        filterDispatch({ type: 'REMOVE_SERVICE_CLASSIFICATION_NAME' })
      })
    } else {
      batch(() => {
        filterDispatch({ type: 'SET_SERVICE_ITEM', payload: id })
        filterDispatch({
          type: 'SET_SERVICE_CLASSIFICATION_NAME',
          payload: name,
        })
      })
    }
  }

  const onServiceClassificationRemove = () => {
    filterDispatch({ type: 'REMOVE_SERVICE_ITEM' })
  }

  const onCountyChange = (e) => {
    filterDispatch({ type: 'SET_SERVICE_COUNTY', payload: e.target.value })
  }

  const onRemoveCustomerBudget = () => {
    filterDispatch({ type: 'REMOVE_CUSTOMER_BUDGET' })
  }
  const onRemoveFilteredName = () => {
    filterDispatch({ type: 'REMOVE_FILTER_NAME' })
  }
  const onRemoveAdvancedFilterItem = (item) => {
    filterDispatch({ type: 'REMOVE_ADVANCED_FILTER_ITEM', payload: item })
  }
  // const onRemoveAllAdvancedFilters = () => {
  //   filterDispatch({ type: 'REMOVE_ALL_ADVANCED_FILTERS' })
  // }

  const [page, setPage] = useState(fallbackData?.page || 1)
  const containerId = 'vender-list-scrollable'
  const [ref, height] = useMeasureHeight()
  const { StickyScroll, isVisible, isSticky } = useStickyFilter(
    containerId,
    height
  )
  const router = useRouter()

  let openModal = null

  const handleOpenModal = () => {
    if (openModal) {
      openModal()
    }
  }

  const { data: serviceClassification } = useServiceClassification(
    filterState.serviceItem
  )

  const { data: filterItemInfos } = useAdvancedFilterItems(() =>
    serviceClassification
      ? { serviceClassification: serviceClassification?._id, limit: 200 }
      : null
  )

  // 使用 useVenderServiceCapacity 檢查是否顯示服務推薦區塊
  const { data: supportServiceData } = useVenderServiceCapacity(
    filterState.serviceItem
      ? {
          serviceClassificationId: filterState.serviceItem,
          county: filterState.serviceCounty,
        }
      : null
  )

  const filterItemMap = useMemo(() => {
    return new Map((filterItemInfos || []).map((item) => [item._id, item.name]))
  }, [filterItemInfos])

  // 當fallbackData.searchText變化時表示發生page route，
  // 此時將頁碼設回1
  useEffect(() => {
    if (
      fallbackData?.searchText &&
      fallbackData?.searchText !== filterState.name
    ) {
      filterDispatch({
        type: 'SET_FILTER_NAME',
        payload: fallbackData.searchText,
      })
      setPage(1)
    }
  }, [fallbackData?.searchText])

  // 讓query string跟著頁碼變動
  useEffect(() => {
    const qs = window.location.href.split('?')?.[1] || ''
    const pageInQs = qs
      .split('&')
      .filter((string) => /^page=/.test(string))
      .map((string) => string.split('=')[1] || '')
      .shift()
    if (parseInt(pageInQs) !== page) {
      const newQs = qs
        .split('&')
        .filter((string) => string && !/^page=/.test(string))
        .concat([`page=${page}`])
        .join('&')
      const path = location.pathname + '?' + newQs
      if (page && !pageInQs) {
        router.replace(path, undefined, { shallow: true })
      } else {
        router.push(path, undefined, { shallow: true })
      }
    }
  }, [page])

  // 讓query string跟著篩選變動
  useDeepCompareEffect(() => {
    const qs = window.location.href.split('?')?.[1] || ''
    const filterValues = []

    for (const [key, value] of Object.entries(filterState)) {
      if (key === 'serviceClassificationName') {
        continue
      }

      if (key === 'serviceCounty') {
        !!value && filterValues.push(`serviceCounty=${value}`)
      } else if (key === 'advancedFilterItems') {
        if (!!value) {
          const queryValue = value?.map((item, index) => {
            const queryValueItem = index === 0 ? item : `${item}+`
            return queryValueItem
          })
          filterValues.push(`${key}=${queryValue.join('')}`)
        }
      } else if (key === 'serviceItem') {
        !!value && filterValues.push(`serviceItem=${value}`)
      } else {
        if (!!value) {
          ;(isNumber(value) || !!value?.length) &&
            filterValues.push(`${key}=${value}`)
        }
      }
    }
    const newQs = qs
      .split('&')
      .filter((string) => string && /^page=/.test(string))
      .concat(filterValues)
      .join('&')
    setPage(1)

    window.history.replaceState(window.history.state, '', `?${newQs}`)
  }, [filterState])

  // 當query變動時，檢查是否需要調整篩選條件
  useEffect(() => {
    if (router.isReady) {
      // 定義所有篩選條件的 key
      const allFilterKeys = [
        'serviceItem',
        'serviceCounty',
        // 'customerBudget',
        // 'advancedFilterItems',
        'name',
      ]

      // 導向初始廠商列表頁
      const handleRouteChange = () => {
        router.replace('/vender', { shallow: true })
      }

      // 確認 url 上的 serviceItem 是否可以出現在篩選列表中
      const handleServiceItem = async (serviceItemId) => {
        try {
          // 先檢查該 serviceItem 是否有效
          const [
            serviceClassificationData,
            error,
          ] = await getServiceClassificationById(serviceItemId)

          // 若不該出現在篩選列表或發生非預期錯誤
          if (
            serviceClassificationData.isVenderFilterListVisible === false ||
            error ||
            !serviceClassificationData
          ) {
            handleRouteChange()
            return
          }

          // 若驗證通過則設置到 filterState
          filterDispatch({ type: 'SET_SERVICE_ITEM', payload: serviceItemId })
        } catch (err) {
          console.error('Failed to validate serviceItem:', err)
          // 發生其他非預期錯誤時也導向初始廠商列表頁
          handleRouteChange()
        }
      }

      // 更新存在於 router.query 中的篩選條件
      for (const [key, value] of Object.entries(router.query)) {
        if (key === 'serviceItem') {
          handleServiceItem(value)
        } else if (key === 'serviceCounty') {
          filterDispatch({ type: 'SET_SERVICE_COUNTY', payload: value })
          // } else if (key === 'customerBudget') {
          //   filterDispatch({ type: 'SET_CUSTOMER_BUDGET', payload: value });
          // } else if (key === 'advancedFilterItems') {
          //   const advancedFilterItems = value.split('+');
          //   advancedFilterItems.forEach((item) => {
          //     filterDispatch({
          //       type: 'TOGGLE_ADVANCED_FILTER_ITEM',
          //       payload: item,
          //     });
          //   });
        } else if (key === 'name' || key === 'keyword') {
          filterDispatch({ type: 'SET_FILTER_NAME', payload: value })
        }
      }

      // 執行 REMOVE 操作，清除不存在於 router.query 中的條件
      allFilterKeys.forEach((key) => {
        if (
          !(key in router.query) &&
          !(key === 'name' && 'keyword' in router.query) // 若 key 是 name，還要檢查 keyword 是否存在
        ) {
          switch (key) {
            case 'serviceItem':
              filterDispatch({ type: 'REMOVE_SERVICE_ITEM' })
              filterDispatch({ type: 'REMOVE_SERVICE_CLASSIFICATION_NAME' })
              break
            case 'serviceCounty':
              filterDispatch({ type: 'REMOVE_SERVICE_COUNTY' })
              break
            // case 'customerBudget':
            //   filterDispatch({ type: 'REMOVE_CUSTOMER_BUDGET' });
            //   break;
            // case 'advancedFilterItems':
            //   filterDispatch({ type: 'REMOVE_ALL_ADVANCED_FILTERS' });
            //   break;
            case 'name':
              filterDispatch({ type: 'REMOVE_FILTER_NAME' })
              break
            default:
              break
          }
        }
      })
    }
  }, [router.isReady, router.query])

  // 讓頁碼隨著query string變動
  useEffect(() => {
    if (!router.isReady) {
      return
    }
    const qs = window.location.href.split('?')?.[1] || ''
    const pageInQs = qs
      .split('&')
      .filter((string) => /^page=/.test(string))
      .map((string) => string.split('=')[1] || '')
      .shift()
    if (pageInQs && parseInt(pageInQs) !== page) {
      setPage(parseInt(pageInQs))
    }
  }, [router.isReady, router.query?.page])

  const {
    data: paginatedVenders,
    isLoading: isPaginatedVendersLoading,
  } = useVenders(() => {
    if (isMobile) {
      return null
    }
    const query = {
      skip: max([page - 1, 0]) * NUMBER_PER_PAGE,
      limit: NUMBER_PER_PAGE,
    }
    return { ...query, ...filterState }
  })
  const { data: venderCount } = useVenderCount(filterState)
  const {
    data: infiniteVenders,
    isLoading: isInfiniteVendersLoading,
    size,
    setSize,
  } = useInfiniteVenders(() => {
    if (!isMobile) {
      return null
    }
    const query = { limit: NUMBER_PER_PAGE }
    return { ...query, ...filterState }
  })
  const isVendersLoading = !isMobile
    ? isPaginatedVendersLoading
    : isInfiniteVendersLoading
  const vendersData = flatten(!isMobile ? paginatedVenders : infiniteVenders)
  const venders = sortBy(vendersData, [
    (vender) => -(vender.sortOrder || 0),
    (vender) => -new Date(vender.createdAt).getTime(),
  ])
  const onLoadMore = () => setSize(size + 1)

  // album content modal
  const webview = useSelector(selectWebview)
  const {
    albumModal,
    closeAlbumModal,
    onAlbumClick,
    onAlbumContentClose,
  } = useAlbumModal('/vender', router, {
    webview,
    nativeHeaderHandler: () => null,
  })

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  return (
    <Layout stickyHeader={<Header />} fixContentHeight>
      <InfiniteContainer id={containerId} filterState={filterState}>
        <StickyScroll isVisible={isVisible} isSticky={isSticky} ref={ref}>
          <ContentWrapper>
            {!isSticky && (
              <Typography
                variant={isLarge ? 'h4' : 'h5'}
                fontWeight="700"
                mt={6}
              >
                服務廠商一覽
              </Typography>
            )}

            <Grid container spacing={0}>
              <Grid item xs={12} p={0}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={isLarge ? 6 : 4}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap={isLarge ? 4 : 2}
                    sx={{
                      ...(isMobile
                        ? {
                            overflow: 'scroll',
                            scrollbarWidth: 'none',
                            scrollBehavior: 'smooth',
                          }
                        : { width: '0px', flex: '1', maxWidth: 'fit-content' }),
                    }}
                  >
                    <ServiceClassificationSelectRadio
                      fullWidth={true}
                      value={filterState.serviceItem}
                      onChange={onServiceClassificationChange}
                      onRemove={onServiceClassificationRemove}
                      size={isLarge ? 'medium' : 'small'}
                      isLarge={isLarge}
                      isMobile={isMobile}
                    />

                    <Select
                      label="縣市"
                      fullWidth={false}
                      size={isLarge ? 'medium' : 'small'}
                      value={filterState.serviceCounty}
                      sx={{ minWidth: 'fit-content' }}
                      onChange={onCountyChange}
                    >
                      {areaData.counties().map((county) => (
                        <option value={county} key={county}>
                          {county}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  {!isLarge ? (
                    <Box
                      position="relative"
                      sx={{ padding: '6px 22px 6px 22px' }}
                      onClick={handleOpenModal}
                    >
                      <Box position="relative">
                        <img
                          width={24}
                          height={24}
                          alt=""
                          src="/img/filter.svg"
                          style={{ display: 'block' }}
                        />
                        {filterState?.name && (
                          <Box
                            width={8}
                            height={8}
                            borderRadius="50%"
                            bgcolor="red"
                            position="absolute"
                            top={-9}
                            right={-9}
                          />
                        )}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Grid>

              {/* 高級篩選 */}
              <HideInDesktopGrid item xs={12}>
                <AdvancedFilterSelection
                  registerOpenModal={(fn) => (openModal = fn)}
                  serviceClassification={filterState.serviceItem}
                  advancedFilterItems={filterState.advancedFilterItems}
                  state={filterState}
                  isLarge={isLarge}
                  dispatch={filterDispatch}
                />
              </HideInDesktopGrid>
            </Grid>
          </ContentWrapper>
        </StickyScroll>

        <ContentWrapper>
          <GutterGrid>
            <HideInMobileGrid item xs={12} lg={3}>
              <Sticky
                topOffset={height}
                isSticky={isSticky}
                isVisible={isVisible}
              >
                <DynamicAdvanceFilters
                  serviceClassificationName={filterState?.name}
                  serviceClassification={filterState.serviceItem}
                  advancedFilterItems={filterState.advancedFilterItems}
                  fallbackData={fallbackData}
                  isLarge={isLarge}
                  isVisible={isVisible}
                  onRemoveFilteredName={onRemoveFilteredName}
                  dispatch={filterDispatch}
                />
              </Sticky>
            </HideInMobileGrid>
            <Grid item xs={12} lg={9} mb={6}>
              {(!!filterState.advancedFilterItems?.length ||
                isString(filterState.name)) && (
                <Box mb={6} mt={4}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{
                      overflowX: 'scroll',
                      scrollbarWidth: 'none',
                      scrollBehavior: 'smooth',
                    }}
                  >
                    {isString(filterState.name) && (
                      <Grid item maxWidth="100%">
                        <Tag>
                          <Box
                            display="flex"
                            flexWrap="noWrap"
                            alignItems="center"
                          >
                            <Typography
                              variant="bodySmall"
                              sx={{
                                ...(!isMobile
                                  ? {
                                      display: 'block',
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      WebkitLineClamp: 1,
                                      whiteSpace: 'nowrap',
                                    }
                                  : {
                                      whiteSpace: 'nowrap',
                                    }),
                              }}
                            >
                              廠商關鍵字：{filterState.name}
                            </Typography>
                            <Box display="flex" onClick={onRemoveFilteredName}>
                              <CloseSVG $ptr width={20} height={20} />
                            </Box>
                          </Box>
                        </Tag>
                      </Grid>
                    )}

                    {filterState.advancedFilterItems?.map((item) => (
                      <Grid item key={item}>
                        <Tag>
                          <Box
                            display="flex"
                            flexWrap="noWrap"
                            alignItems="center"
                          >
                            <Typography variant="caption">
                              {filterItemMap.get(item)}
                            </Typography>
                            <Box
                              display="flex"
                              onClick={() => onRemoveAdvancedFilterItem(item)}
                            >
                              <CloseSVG $ptr width={12} height={12} />
                            </Box>
                          </Box>
                        </Tag>
                      </Grid>
                    ))}
                    {/* {isLarge && (
                      <Grid item>
                        <Typography
                          onClick={onRemoveAllAdvancedFilters}
                          sx={{cursor: 'pointer'}}
                        >
                          清除所有條件
                        </Typography>
                      </Grid>
                    )} */}
                  </Grid>
                </Box>
              )}

              <Box
                display="flex"
                justifyContent="end"
                alignItems="center"
                mb={isLarge ? 6 : 4}
                mt={6}
              >
                {isLarge && !!venders?.length && (
                  <Box ml={isLarge ? 12 : 0} minWidth="fit-content">
                    <Pagination
                      page={page}
                      setPage={setPage}
                      pageAmount={Math.ceil(venderCount / NUMBER_PER_PAGE)}
                      urlPath={LS_APP_DOMAIN + '/vender'}
                    />
                  </Box>
                )}
              </Box>

              {supportServiceData?.isSupport && (
                <Link
                  href={`/service/${serviceClassification?.identityCode}`}
                  onClick={() => {
                    GtmAgent.directSendToGTM({
                      event: 'living_action',
                      living_event_name: 'find_supplier_click',
                      ...(filterState?.serviceCounty && {
                        living_filter_county: filterState.serviceCounty,
                      }),
                      ...(filterState?.serviceClassificationName && {
                        living_filter_service:
                          filterState.serviceClassificationName,
                      }),
                      ...(filterState?.name && {
                        living_search_keyword: filterState.name,
                      }),
                    })
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    backgroundColor="primary.bgPlus2"
                    border={1}
                    borderColor="primary.minus2"
                    borderRadius={1.5}
                    gap={2}
                    p={5}
                  >
                    <Typography
                      variant="paraBodySmall"
                      display="flex"
                      alignItems="center"
                      color="text.minus2"
                    >
                      <NowrapDiv $lines={3}>
                        不知道找哪間廠商？讓信義居家幫您找廠商，點擊前往{' '}
                        {filterState.serviceClassificationName} 預約
                      </NowrapDiv>
                    </Typography>

                    <Box width={24} height={24}>
                      <ArrowRight color="primary.main" />
                    </Box>
                  </Box>
                </Link>
              )}

              {!isVendersLoading && !venders?.length && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  my={25}
                >
                  <Img src="/img/default-search-off.svg" />
                  <Box mt={5} mb={3} textAlign="center">
                    <Typography variant="h6">找不到合適的廠商嗎？</Typography>

                    <Typography color="textSecondary" mt={3} mb={3}>
                      請服務人員幫你找吧～
                    </Typography>
                  </Box>
                </Box>
              )}

              {!isVendersLoading && !!venders?.length && (
                <InfiniteScroll
                  dataLength={venders?.length || 0}
                  hasMore={true}
                  next={onLoadMore}
                  scrollThreshold={0.3}
                  scrollableTarget={containerId}
                >
                  {venders?.map((vender) => (
                    <LazyLoad
                      height={80}
                      key={vender._id}
                      scrollContainer={`#${containerId}`}
                    >
                      <VenderListItem
                        vender={vender}
                        onAlbumClick={onAlbumClick}
                        isLarge={isLarge}
                        filterState={filterState}
                      />
                    </LazyLoad>
                  ))}
                </InfiniteScroll>
              )}

              {isLarge && !!venders?.length && (
                <Box display="flex" justifyContent="center" mt={6}>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    pageAmount={Math.ceil(venderCount / NUMBER_PER_PAGE)}
                  />
                </Box>
              )}
              <Typography
                variant="paraBodySmall"
                color="text.secondary"
                mt={16}
              >
                *{VENDER_AD_WARNING_TEXT}
              </Typography>
            </Grid>
          </GutterGrid>
        </ContentWrapper>

        {webview?.name !== 'lsApp' && <Footer />}
      </InfiniteContainer>

      {router?.isReady && albumModal.open && (
        <FullscreenModal open={albumModal.open} onClose={closeAlbumModal}>
          <DynamicAlbumContent
            onClose={onAlbumContentClose}
            closeModal={closeAlbumModal}
            fallbackData={albumModal.fallbackData}
          />
        </FullscreenModal>
      )}
    </Layout>
  )
}

VenderList.defaultProps = {
  isMobile: true,
}

export default VenderList
